import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { MetaGamesEventType } from '../../dto/metagames.types';
import type { MetagamesCurrentEvent, MetagamesResponse } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { type ClaimPlinkoAnimationState } from './types';

export class PlinkoStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public plinkoEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setPlinkoEvent(event: MetagamesCurrentEvent | undefined) {
    this.plinkoEvent = event;
  }

  @observable
  public currentOpenPlinkoEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenPlinkoEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenPlinkoEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  public startDialogs(type?: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (type === 'Plinko') {
      void this.startPlinkoGame();
      return true;
    }

    return false;
  }

  @action
  private updateOptInStatus() {
    if (this.plinkoEvent) {
      const metaCoinType = this.rootStore.coinTypeStore.coinType.toLocaleLowerCase() as 'gc' | 'sc';
      const current = this.plinkoEvent[metaCoinType];
      const updated = {
        ...this.plinkoEvent,
        [metaCoinType]: {
          ...current,
          isOptedIn: true,
        },
      };
      this.setPlinkoEvent(updated);
    }
  }

  public async acknowledgeOptIn() {
    try {
      const { coinType } = this.rootStore.coinTypeStore;
      const eventId = this.plinkoEvent?.id;
      void this.updateOptInStatus();
      await postRequest<{ data: MetagamesResponse }>('metagame-events/opt-in', {
        coinType,
        eventId,
      });
    } catch (error) {
      devError('PlinkoStore: acknowledgeOptIn error', error);
    }
  }

  @computed
  public get eventByCoin() {
    if (!this.plinkoEvent) {
      return undefined;
    }
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;
    return this.plinkoEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];
  }

  public async startPlinkoGame() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.plinkoEvent) {
        devError('PlinkoStore: startPlinkoGame error: plinko Event not found');
        return;
      }

      const eventByCoin = this.plinkoEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('PlinkoStore: startPlinkoGame error: event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('PlinkoStore: startPlinkoGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (metagames) {
        const { token, path } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          metagamesToken: gameUiFlag ? token : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.plinkoEvent,
          token,
          path,
          url: metagamesUrl,
          startDate: this.plinkoEvent.startDate,
          endDate: this.plinkoEvent.endDate,
        };

        this.setCurrentOpenPlinkoEvent(metagamesEventToSet);

        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenMetagamesDialog,
          options: { type: MetaGamesEventType.PLINKO },
        });
      } else {
        devError('PlinkoStore: startPlinkoGame error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('PlinkoStore: startPlinkoGame error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame(dialogType?: FeatureManagerDialogType) {
    this.setCurrentOpenPlinkoEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  @observable
  public claimPlinkoAnimationState: ClaimPlinkoAnimationState = {
    plinkoAmount: 0,
    enable: false,
  };

  @action
  public setClaimPlinkoAnimationState(state: Partial<ClaimPlinkoAnimationState>) {
    this.claimPlinkoAnimationState = { ...this.claimPlinkoAnimationState, ...state };
  }

  public startClaimPlinkoAnimation({
    plinkoAmount,
    onAnimationEnd,
    coinType,
  }: {
    plinkoAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
    coinType?: CoinType;
  }) {
    this.setClaimPlinkoAnimationState({
      plinkoAmount,
      enable: true,
      coinType,
      onAnimationEnd: () => {
        this.setClaimPlinkoAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }

  public handlePlinkoIframeLoad(iframeRef: HTMLIFrameElement) {
    if (!iframeRef.contentWindow) {
      devError('PlinkoStore handleTrailIframeLoad: Iframe content window is not available');
      return;
    }

    this.rootStore.metagamesStore.setIframeRef(iframeRef);

    if (this.currentOpenPlinkoEvent) {
      iframeRef.contentWindow.postMessage(
        {
          type: 'newMetagamesEvent',
          payload: {
            metagamesEvent: toJS(this.currentOpenPlinkoEvent),
          },
        },
        import.meta.env.VITE_GAME_UI_URL,
      );
    }
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: 'plinko',
    });
  }
}
